/**
 * Public marketing routes: the landing page and the /playbook gift page.
 *
 * Split out of App.tsx (500-line ceiling). Both pages are lazy chunks so the
 * app's entry bundle does not carry the marketing page and its scenes.
 *
 * The landing page carries its OWN Suspense boundary on purpose: App.tsx wraps
 * it in ShopifyInstallIntercept, whose mount effect bounces a Shopify
 * managed-install load to OAuth. If the lazy page suspended the outer
 * boundary instead, the intercept would not mount — and not redirect — until
 * the landing chunk had downloaded. With the boundary here, the intercept
 * commits immediately and only the page body waits.
 *
 * `marketingRoutes` is a Fragment interpolated into the public Routes tree
 * (like adminRoutes), which rejects non-Route children. Remember the other
 * half of a public page: app/publicPaths.ts must list it too.
 *
 * Suspense fallback is null (not PageLoader) so a refresh of /, /faq,
 * /channels, /flows or /playbook does not flash the branded loader.
 */
import { lazy, Suspense, type ReactNode } from 'react';
import { Route } from 'react-router-dom';

const Landing = lazy(() => import('../pages/Landing'));
const Playbook = lazy(() => import('../pages/Playbook'));

function MarketingSuspense({ children }: { children: ReactNode }): JSX.Element {
  return <Suspense fallback={null}>{children}</Suspense>;
}

export function LandingPage(): JSX.Element {
  return (
    <MarketingSuspense>
      <Landing />
    </MarketingSuspense>
  );
}

function PlaybookPage(): JSX.Element {
  return (
    <MarketingSuspense>
      <Playbook />
    </MarketingSuspense>
  );
}

export const marketingRoutes = (
  <>
    {/* Landing section deep links — same page, scroll to section on mount. */}
    <Route path="/faq" element={<LandingPage />} />
    <Route path="/channels" element={<LandingPage />} />
    <Route path="/flows" element={<LandingPage />} />
    {/* The free Automation Playbook the IG/WhatsApp gift flows send. */}
    <Route path="/playbook" element={<PlaybookPage />} />
    <Route path="/playbook/:kind" element={<PlaybookPage />} />
  </>
);
